/* Announcement slider -- vanilla, no slider library.
 *
 * The theme does bundle Splide (SplideComponent in assets/secondary.js), but it is not used here:
 * it imposes its own markup and inline slide widths over the required viewport/track/translateX
 * structure, and it exposes no handle for removing a slide from rotation at runtime, which the
 * countdown expiry behaviour needs.
 *
 * Seamless infinite loop
 * ----------------------
 * A plain translateX(-100% * index) has to fly the whole track back at the wrap point. Instead the
 * track is padded with clones:
 *
 *     [clone of LAST] [slide 0] [slide 1] ... [slide N-1] [clone of FIRST]
 *
 * so real slide i lives at DOM position i + 1. Advancing past the last slide moves onto the trailing
 * clone -- still travelling leftward, no reversal -- and once that transition ends we disable the
 * transition, jump to the identical real slide 0, and re-enable it. The reposition is invisible
 * because the two are pixel-identical. The same trick in reverse covers stepping back off slide 0.
 *
 * Direction is not stateful: autoplay always steps forward (content travels right-to-left). The
 * right arrow is a one-time reverse step only; it never flips the autoplay direction.
 */
const SLIDER_WRAP_SAFETY_MS = 80;

class AnnouncementSlider extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.teardown();
  }

  init() {
    this.teardown();

    this.track = this.querySelector('[data-slider-track]');
    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');
    if (!this.track) return;

    /* Real slides only -- clones are rebuilt from these and never counted as originals.
       The class is used as a fallback because everything here depends on finding the slides: when
       the data attribute went missing once, the early return below silently disabled the arrows and
       the countdown at the same time. Degrading to the class keeps a markup regression from taking
       the whole controller down with it. */
    const collect = (selector) =>
      Array.from(this.track.querySelectorAll(selector)).filter(
        (slide) => !slide.hasAttribute('data-slider-clone')
      );
    this.slides = collect('[data-announcement-slide]');
    if (this.slides.length === 0) this.slides = collect('.announcement-slider__slide');
    if (this.slides.length === 0) return;

    this.autorotate = this.dataset.autorotate === 'true';
    this.intervalMs = (parseFloat(this.dataset.interval) || 5) * 1000;
    this.index = 0;
    this.isWrapping = false;

    this.onPrev = () => this.step(1); // Left arrow advances, matching the autoplay direction.
    this.onNext = () => this.step(-1); // Right arrow is the only reversing control.
    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

    this.initCountdowns();
    this.rebuildLoop();

    // Enable the transition only after the first paint, so the initial position does not animate.
    requestAnimationFrame(() => this.classList.add('announcement-slider--animated'));

    if (this.hasCountdowns) {
      this.tick();
      this.tickTimer = setInterval(() => this.tick(), 1000);
    }

    this.startAutoRotate();
  }

  teardown() {
    clearInterval(this.autoRotateTimer);
    clearInterval(this.tickTimer);
    clearTimeout(this.wrapTimer);
    if (this.prevButton && this.onPrev) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton && this.onNext) this.nextButton.removeEventListener('click', this.onNext);
  }

  /* ---- slides ----------------------------------------------------------- */

  // Only slides not hidden by an expired countdown take part in rotation.
  visibleSlides() {
    return this.slides.filter((slide) => !slide.hidden);
  }

  /* Rebuilds the leading/trailing clones for the current visible set. Called on init and whenever a
     slide is hidden, so a hidden slide can never linger on as a clone. */
  rebuildLoop() {
    this.track.querySelectorAll('[data-slider-clone]').forEach((clone) => clone.remove());

    const visible = this.visibleSlides();
    this.canLoop = visible.length > 1;

    if (this.canLoop) {
      this.track.insertBefore(this.makeClone(visible[visible.length - 1]), visible[0]);
      this.track.appendChild(this.makeClone(visible[0]));
    }

    if (this.index >= visible.length) this.index = 0;
    if (this.index < 0) this.index = Math.max(0, visible.length - 1);

    this.applyPosition(this.index);
    this.syncControls();
  }

  makeClone(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-slider-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    // Keep duplicated links out of the tab order.
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    return clone;
  }

  /* Position is expressed in DOM slots. With clones present, real slide i sits at slot i + 1, so
     slot -1+1 = 0 is the leading clone and slot N+1 is the trailing clone. */
  applyPosition(logicalIndex) {
    const offset = this.canLoop ? logicalIndex + 1 : logicalIndex;
    this.track.style.setProperty('--slide-index', offset);
  }

  syncControls() {
    const isStatic = this.visibleSlides().length <= 1;
    this.classList.toggle('announcement-slider--static', isStatic);
    if (this.prevButton) this.prevButton.hidden = isStatic;
    if (this.nextButton) this.nextButton.hidden = isStatic;
    this.applyArrowColor();
  }

  /* Arrows sit outside the clipped viewport and are shared by every slide, so their colour is
     applied here from the active slide's setting rather than rendered per block. */
  applyArrowColor() {
    const active = this.visibleSlides()[this.index];
    if (!active) return;
    const color = active.dataset.arrowColor;
    if (color) this.style.setProperty('--announcement-arrow-color', color);
  }

  transitionMs() {
    // Read the real duration so prefers-reduced-motion (0s) normalises immediately instead of
    // waiting on a transitionend that will never fire.
    const declared = getComputedStyle(this.track).transitionDuration || '0s';
    const first = declared.split(',')[0].trim();
    const value = parseFloat(first) || 0;
    return first.endsWith('ms') ? value : value * 1000;
  }

  /* direction: +1 advances (content travels right-to-left), -1 reverses. */
  step(direction) {
    const visible = this.visibleSlides();
    if (visible.length <= 1) return;
    // Ignore input mid-wrap so a fast double click cannot desync the position.
    if (this.isWrapping) return;

    this.move(direction);
    // Manual interaction pauses auto-rotation.
    this.stopAutoRotate();
  }

  move(direction) {
    const visible = this.visibleSlides();
    if (visible.length <= 1) return;

    const target = this.index + direction;
    const wrapsForward = target >= visible.length;
    const wrapsBackward = target < 0;

    if (!this.canLoop || (!wrapsForward && !wrapsBackward)) {
      this.index = (target + visible.length) % visible.length;
      this.applyPosition(this.index);
      this.applyArrowColor();
      return;
    }

    // Travel onto the clone in the same direction -- no snap back -- then silently land on the
    // identical real slide once the animation has finished.
    this.isWrapping = true;
    this.applyPosition(target);

    const landing = wrapsForward ? 0 : visible.length - 1;
    const settle = () => {
      if (!this.isWrapping) return;
      this.isWrapping = false;
      this.index = landing;
      this.classList.remove('announcement-slider--animated');
      this.applyPosition(this.index);
      // Force a reflow so removing and re-adding the transition cannot be batched into one frame,
      // which would animate the reposition.
      void this.track.offsetWidth;
      this.classList.add('announcement-slider--animated');
      this.applyArrowColor();
    };

    clearTimeout(this.wrapTimer);
    this.wrapTimer = setTimeout(settle, this.transitionMs() + SLIDER_WRAP_SAFETY_MS);
    // The clone is visually the destination slide, so recolour immediately rather than after settle.
    const previewSource = wrapsForward ? visible[0] : visible[visible.length - 1];
    if (previewSource && previewSource.dataset.arrowColor) {
      this.style.setProperty('--announcement-arrow-color', previewSource.dataset.arrowColor);
    }
  }

  /* ---- auto-rotate ------------------------------------------------------ */

  startAutoRotate() {
    clearInterval(this.autoRotateTimer);
    if (!this.autorotate) return;
    if (this.visibleSlides().length <= 1) return;
    // Always forward: autoplay direction never depends on which arrow was last used.
    this.autoRotateTimer = setInterval(() => this.move(1), this.intervalMs);
  }

  stopAutoRotate() {
    clearInterval(this.autoRotateTimer);
  }

  /* ---- countdowns (display only -- never touches pricing or product data) */

  initCountdowns() {
    this.hasCountdowns = this.slides.some((slide) => slide.dataset.countdownEnd);
  }

  // Parses YYYY-MM-DDTHH:MM(:SS) as the visitor's local time. Falling back to the Date parser keeps
  // an unexpected format from throwing.
  parseEndTime(value) {
    const parts = String(value)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!parts) return new Date(String(value).replace(/-/g, '/'));
    return new Date(
      parseInt(parts[1], 10),
      parseInt(parts[2], 10) - 1,
      parseInt(parts[3], 10),
      parseInt(parts[4], 10),
      parseInt(parts[5], 10),
      parseInt(parts[6] || '0', 10)
    );
  }

  tick() {
    const now = Date.now();
    let visibilityChanged = false;

    // Hold the element on screen, not its index: hiding an earlier slide shifts every index after
    // it, which would otherwise bump the viewer onto a different slide.
    const currentSlide = this.visibleSlides()[this.index];

    // Every countdown element in the DOM is rendered, clones included. They derive from the same end
    // time, so clones stay in sync without any extra bookkeeping.
    this.track.querySelectorAll('[data-countdown-end]').forEach((slide) => {
      const endTime = this.parseEndTime(slide.dataset.countdownEnd);
      if (!(endTime instanceof Date) || isNaN(endTime.getTime())) return;

      const remainingMs = endTime.getTime() - now;
      this.renderCountdown(slide, Math.max(0, Math.floor(remainingMs / 1000)));

      if (remainingMs <= 0 && slide.dataset.countdownBehavior === 'hide' && !slide.hidden) {
        slide.hidden = true;
        if (!slide.hasAttribute('data-slider-clone')) visibilityChanged = true;
      }
    });

    if (visibilityChanged) {
      // Stay on the same slide where possible; if it was the one just hidden, fall back to the
      // first remaining slide.
      const position = this.visibleSlides().indexOf(currentSlide);
      this.index = position === -1 ? 0 : position;
      this.rebuildLoop();
      this.startAutoRotate();
    }
  }

  renderCountdown(slide, totalSeconds) {
    const output = slide.querySelector('[data-countdown-time]');
    if (!output) return;
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, '0');
    output.textContent =
      days > 0
        ? `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
        : `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }
}

customElements.define('announcement-slider', AnnouncementSlider);

// Re-initialise when the theme editor re-renders the section.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('announcement-slider').forEach((slider) => slider.init());
});
